import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { loadAccountTradeInfoAction } from "../../app/appActions";
import { getAccountsTradeInfoSelector } from "../../app/appSelectors";
import { Loader } from "../../components/Loader/Loader";
import amountFormat from "../../utils/amountFormat.js";
import { InfoIcon } from "../../components/InfoIcon/InfoIcon.jsx";
import moment from "moment";
import { Accordion, Alert, Badge, Button, Form, FormGroup, Modal } from "react-bootstrap";
import {
    NewLifeBlockStyled,
    NewLifeCurrentEquityStyled,
    NewLifeNoPositionsStyled,
    NewLifePositionsStyled,
    NewLifeRefundAmountInfoStyled,
    NewLifeRefundAmountStyled,
    NewLifeRefundDepositInfoStyled,
    NewLifeRespawnModalStyled
} from "./styled/NewLifeAccountButtonStyled.js";
import { BinIcon } from "../../components/icon/Icon.jsx";
import { setNewLifeRespawnAction } from "../Bonus/bonusesActions.js";
import {
    getNewLifeRespawnError,
    getNewLifeRespawnInProgress,
    getNewLifeRespawnSuccess
} from "../Bonus/bonusesSelectors.js";
import { BREAKPOINTS, RouterPaths } from "../../app/appConstants.js";
import { Link } from "react-router-dom";
import { useMaxWidthMediaQuery } from "../../components/responsive/responsive-hooks.js";
import { AccordionToggle } from "../../components/AccordionToggle/AccordionToggle.jsx";
import { useGTMEvent } from "../GoogleTagManager/googleTagManagerHooks.js";
import parseAccountName from "../../utils/parseAccountName.js";
import { useLangContext } from "../../providers/LangProvider/LangProviderContext.js";

function NewLifeAccountPanel(props) {
    const {
        accountsTradeInfo,
        loadAccountTradeInfoAction,
        selectedAccount,
        setNewLifeRespawnAction,
        respawnError,
        respawnInProgress,
        respawnSuccess,
        onRespawnSuccess,
    } = props;

    const isSmallMaxWidth = useMaxWidthMediaQuery(BREAKPOINTS.small);
    const {getLangPath} = useLangContext();
    const {handleGTMClick} = useGTMEvent();

    useEffect(() => {
        if (selectedAccount?.account && selectedAccount.isAvailable) {
            loadAccountTradeInfoAction(selectedAccount?.account);
        }
    }, [loadAccountTradeInfoAction, selectedAccount, respawnSuccess]);

    const {
        isLoading,
        data
    } = useMemo(() => accountsTradeInfo[selectedAccount.account] || {}, [accountsTradeInfo, selectedAccount.account]);
    const [showModal, setShowModal] = useState(false);

    const plColor = useCallback((amount, pos, neg) => {
        return <span
                style={{color: amount > 0 ? 'var(--color-positive)' : amount < 0 ? 'var(--color-negative)' : ''}}>{amount < 0 ? neg || amount : pos || amount}</span>
    }, []);

    const openModal = useCallback((e) => {
        e.preventDefault();
        setShowModal(true);
        const parsedAccount = parseAccountName(selectedAccount.account);
        handleGTMClick({
            module: 'RespawnPage',
            button: 'respawn',
            account_type: parsedAccount.type,
        })
    }, [handleGTMClick, selectedAccount]);

    const closeModal = useCallback(() => {
        setShowModal(false);
    }, []);

    const respawnHandler = useCallback((e) => {
        e.preventDefault();
        setNewLifeRespawnAction({
            accountCode: selectedAccount.account,
            email: e.currentTarget.querySelector('[name=email-confirmation]').value,
        });
        const parsedAccount = parseAccountName(selectedAccount.account);
        handleGTMClick({
            module: 'RespawnPage',
            button: 'confirm_respawn',
            account_type: parsedAccount.type,
        })
    }, [selectedAccount, setNewLifeRespawnAction, handleGTMClick]);

    useEffect(() => {
        if (respawnSuccess) {
            closeModal();
            onRespawnSuccess();
        }
    }, [respawnSuccess, closeModal, onRespawnSuccess]);

    const handleAccordionOpen = useCallback(() => handleGTMClick({
        module: 'RespawnPage',
        button: 'positions_list',
        action: 'Open',
    }), [handleGTMClick])

    const handleAccordionClose = useCallback(() => handleGTMClick({
        module: 'RespawnPage',
        button: 'positions_list',
        action: 'Close',
    }), [handleGTMClick])

    const tradingClickHandler = useCallback(() => handleGTMClick({
        module: 'RespawnPage',
        button: 'go_trading',
    }), [handleGTMClick])

    return <div>
        <Loader show={isLoading}/>
        {
            <Modal show={showModal && selectedAccount.isAvailable}
                   onHide={closeModal}
                   centered
                   fullscreen={!!isSmallMaxWidth}>
                <Modal.Header style={{background: 'transparent'}} closeButton/>
                <NewLifeRespawnModalStyled>
                    <Form onSubmit={respawnHandler}>
                        <p dangerouslySetInnerHTML={{__html: getLangPath('bonus.new_life.confirm_amount_info', {amount: selectedAccount.amountForRespawn})}}/>
                        <Alert variant={'warning'}>
                            <BinIcon name={'IcExclamationMarkTriangle'} className="me-3"/>
                            <div dangerouslySetInnerHTML={{__html: getLangPath('bonus.new_life.confirm_notice')}}/>
                        </Alert>
                        <p dangerouslySetInnerHTML={{__html: getLangPath('bonus.new_life.confirm_enter_email')}}/>
                        <Form.Control className={'mb-3'} type={'text'} name={'email-confirmation'}
                                      readOnly={respawnInProgress}
                                      autoComplete={'off'}
                                      placeholder={getLangPath('bonus.new_life.confirm_email_placeholder')}
                                      isInvalid={!!respawnError}
                        />
                        {
                                respawnError && <FormGroup className="mt-3">
                                    <div className="text-danger">{getLangPath(respawnError)}</div>
                                </FormGroup>
                        }
                        <center>
                            <Button variant={'outline-warning'} type='submit'
                                    disabled={respawnInProgress}>{getLangPath('bonus.new_life.confirm_button')} <BinIcon
                                    name={'IcArrowRight'}/></Button>
                        </center>
                    </Form>
                </NewLifeRespawnModalStyled>
                <Loader show={respawnInProgress}/>
            </Modal>
        }

        {
                !selectedAccount.respawned && <>
                    <NewLifeCurrentEquityStyled>
                        {getLangPath('bonus.new_life.current_equity')} <span>${amountFormat(selectedAccount.equity)}</span>
                    </NewLifeCurrentEquityStyled>
                </>
        }

        {
                (!!selectedAccount.respawned || selectedAccount.isAvailable) && <>
                    <NewLifeRefundAmountStyled>
                        {
                                selectedAccount.maxDeposit && <span>
                                        <InfoIcon>
                                            <NewLifeRefundAmountInfoStyled>
                                                <div className={'mb-2'}>
                                                    {getLangPath('bonus.new_life.max_deposit_info_tooltip', {amount: selectedAccount.maxRespawnAmount})}
                                                </div>
                                                <NewLifeRefundDepositInfoStyled>
                                                    <div>
                                                        <div>{selectedAccount.maxDeposit.payment_name}</div>
                                                        <div>{selectedAccount.maxDeposit.type}</div>
                                                    </div>
                                                    <div className={'text-right'}>
                                                        <div>${amountFormat(selectedAccount.maxDeposit.amount)}</div>
                                                        <div>{moment(selectedAccount.maxDeposit.completed_at).format(getLangPath('format.date_time'))}</div>
                                                    </div>
                                                </NewLifeRefundDepositInfoStyled>
                                            </NewLifeRefundAmountInfoStyled>
                                        </InfoIcon>
                                    </span>
                        }
                        <p>{getLangPath(!selectedAccount.respawned ? 'bonus.new_life.after_respawning' : 'bonus.new_life.equity_restored')}</p>
                        <div>${amountFormat(!selectedAccount.respawned ? selectedAccount.amountForRespawn : selectedAccount.respawned.amount)}</div>
                    </NewLifeRefundAmountStyled>
                </>
        }

        {
                !selectedAccount.respawned && data && <>
                    {
                            !data.positions.length && <NewLifeNoPositionsStyled>
                                {getLangPath('bonus.new_life.positions_empty')}
                            </NewLifeNoPositionsStyled>
                    }

                    {
                            !!data.positions.length && <NewLifePositionsStyled>
                                <Accordion>
                                    <Accordion.Item eventKey="0">
                                        <AccordionToggle
                                                eventKey="0"
                                                onClose={handleAccordionClose}
                                                onOpen={handleAccordionOpen}>
                                            <div className="flex-row justify-content-between">
                                                <div><Badge bg={'info'}
                                                            className={'me-2'}>{parseInt(data.metrics.openPositionsCount || 0) + parseInt(data.metrics.openOrdersCount || 0)}</Badge>
                                                    {getLangPath('bonus.new_life.accordion_title')}
                                                </div>
                                                <div className={'text-right pl-title'}>{getLangPath('bonus.new_life.pl')} <span
                                                        className="pl">{plColor(amountFormat(data.metrics.openPL))}</span>
                                                </div>
                                            </div>
                                        </AccordionToggle>
                                        <Accordion.Body>
                                            {
                                                    data.metrics.openPositionsCount > 0 &&
                                                    <div className="position-header">{getLangPath('bonus.new_life.accordion_positions_title')}</div>
                                            }
                                            {
                                                data.positions.map(item => <Fragment key={item.positionCode}>
                                                    <div className="flex-row justify-content-between position-row">
                                                        <div className={'text-left'}><span
                                                                className="symbol">{item.symbol}</span><span
                                                                className="side">{plColor(item.quantity, getLangPath('bonus.new_life.position_buy'), getLangPath('bonus.new_life.position_sell'))}</span>
                                                        </div>
                                                        <div className={'text-right'}><span
                                                                className="pl">{plColor(amountFormat(item.fpl))}</span>
                                                        </div>
                                                    </div>
                                                </Fragment>)
                                            }
                                            {
                                                    data.metrics.openOrdersCount > 0 &&
                                                    <div className="position-header">{getLangPath('bonus.new_life.accordion_orders_title')}</div>
                                            }
                                            {
                                                data.orders.map(item => <Fragment key={item.orderId}>
                                                    <div className="flex-row justify-content-between position-row">
                                                        <div className={'text-left'}><span
                                                                className="symbol">{item.instrument}</span><span
                                                                className="side">{plColor(item.side === 'BUY' ? 1 : -1, getLangPath('bonus.new_life.position_buy'), getLangPath('bonus.new_life.position_sell'))}</span>
                                                        </div>
                                                    </div>
                                                </Fragment>)
                                            }
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </NewLifePositionsStyled>
                    }

                    <Form.Group>
                        <Button variant={'outline-danger'} size={'lg'}
                                onClick={openModal}>{getLangPath('bonus.new_life.respawn_button')}</Button>
                    </Form.Group>
                </>
        }

        {
                !!selectedAccount.respawned && <NewLifeBlockStyled>
                    <Alert variant={'success'} className={'alert-lg text-left'}>
                        <BinIcon name={'IcCheckMarkCircleGreen'} className="me-3"/>
                        <div className="flex-row justify-content-between">
                            <div>{getLangPath('bonus.new_life.congratulations')}</div>
                            <div className={'text-right'}>{moment(selectedAccount.respawned.completed_at).format(getLangPath('format.date_time'))}</div>
                        </div>
                    </Alert>

                    <Form.Group className={'mt-3'}>
                        <Link to={RouterPaths.main}>
                            <Button variant={'outline-warning'} size={'lg'}>
                                {getLangPath('bonus.new_life.go_trading')} <BinIcon name={'IcArrowRight'}/>
                            </Button>
                        </Link>
                    </Form.Group>
                </NewLifeBlockStyled>
        }

        {
                !selectedAccount.respawned && !selectedAccount.isAvailable && <NewLifeBlockStyled>
                    <Alert variant={'info'} className={'alert-lg text-left'}>
                        <div className="d-flex">
                            <div className="d-flex align-items-center me-3">
                                <BinIcon name={'IcInfoCircle'}/>
                            </div>
                            <div>{getLangPath(`bonus.new_life.reasons.${selectedAccount.reason}`)}</div>
                        </div>
                    </Alert>

                    <Form.Group className={'mt-3'}>
                        <Link to={RouterPaths.main} onClick={tradingClickHandler}>
                            <Button variant={'outline-warning'} size={'lg'}>
                                {getLangPath('bonus.new_life.go_trading')} <BinIcon name={'IcArrowRight'}/>
                            </Button>
                        </Link>
                    </Form.Group>
                </NewLifeBlockStyled>
        }
    </div>
}

const mapStateToProps = createStructuredSelector({
    accountsTradeInfo: getAccountsTradeInfoSelector,
    respawnError: getNewLifeRespawnError,
    respawnInProgress: getNewLifeRespawnInProgress,
    respawnSuccess: getNewLifeRespawnSuccess,
});

const mapDispatchToProps = {
    loadAccountTradeInfoAction,
    setNewLifeRespawnAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(NewLifeAccountPanel);
